import { TRANSFER_LIST_MIN_SEARCH_PHRASE_LENGTH } from '@openx/components-core/lib/SearchTransferList/constants';
import type { TransferListItemsMap } from '@openx/components-core/lib/SearchTransferList/types';
import type {
  UseSeatTransferListItemsFetchParameters,
  UseSeatTransferListItemsFetchReturnType,
} from '@openx/components-core/lib/SearchTransferList/variants/SeatTransferList/types';
import every from 'lodash/every';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { useMemo } from 'react';
import { useSeatOptionsFetch } from '../../../hooks/useSeatOptionsFetch';
import type { CuratedDealBuyerDiscountSeat } from '../../types';

export const useSeatTransferListItemsFetch = ({
  chipFilters,
  itemsToIgnore,
  searchPhrase,
}: UseSeatTransferListItemsFetchParameters<CuratedDealBuyerDiscountSeat>): UseSeatTransferListItemsFetchReturnType<CuratedDealBuyerDiscountSeat> => {
  const hasEmptyFilters = every(chipFilters, value => value === null);
  const hasEmptySearchPhrase = searchPhrase.length < TRANSFER_LIST_MIN_SEARCH_PHRASE_LENGTH;
  const isDisabled = hasEmptyFilters && hasEmptySearchPhrase;

  const { isLoading: isLoadingSeatOptionsByName, mapById: seatFoundByNameById } = useSeatOptionsFetch(isDisabled, {
    buyer_id: chipFilters.buyer_id?.toString(),
    demand_partner_id: chipFilters.demand_partner_id?.toString(),
    name__contains: searchPhrase,
  });

  const { isLoading: isLoadingSeatOptionsBySeatId, mapById: seatFoundBySeatIdById } = useSeatOptionsFetch(isDisabled, {
    buyer_id: chipFilters.buyer_id?.toString(),
    demand_partner_id: chipFilters.demand_partner_id?.toString(),
    seat_id: searchPhrase,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to recalculate this map when upstream data changes (seatFoundByNameById, seatFoundBySeatIdById)
  const seatById = useMemo<TransferListItemsMap<CuratedDealBuyerDiscountSeat>>(() => {
    return omit(merge({}, seatFoundByNameById, seatFoundBySeatIdById), Object.keys(itemsToIgnore));
  }, [seatFoundByNameById, seatFoundBySeatIdById]);

  return {
    data: seatById,
    isFetching: isLoadingSeatOptionsByName || isLoadingSeatOptionsBySeatId,
  };
};
